import { Calendar, Mail01, Trash01 } from '@untitledui/icons';

import { ConfirmationDialog } from '@/lib/base/overlays';

import type { PendingBulk } from './list-panel/types';
import type { ApproveGroupRisk } from './utils/approveGroupRisk';

interface BulkResolutionConfirmDialogsProps {
  pendingApprove: PendingBulk | null;
  pendingRisk: ApproveGroupRisk | null;
  pendingCount: number;
  onConfirmApprove: () => void;
  onCancelApprove: () => void;
  pendingDismiss: PendingBulk | null;
  dismissCount: number;
  onConfirmDismiss: () => void;
  onCancelDismiss: () => void;
}

// Risk-gated confirmation dialogs for the approve-group / dismiss-group bulk
// operations (whole priority group, or the cross-group selection — the
// caller resolves scope up front onto `pendingApprove`/`pendingDismiss`).
export function BulkResolutionConfirmDialogs({
  pendingApprove,
  pendingRisk,
  pendingCount,
  onConfirmApprove,
  onCancelApprove,
  pendingDismiss,
  dismissCount,
  onConfirmDismiss,
  onCancelDismiss,
}: BulkResolutionConfirmDialogsProps) {
  return (
    <>
      <ConfirmationDialog
        isOpen={pendingApprove !== null}
        title={`${pendingApprove?.tier === 'selected' ? 'Approve' : 'Approve all'} ${pendingCount} action${pendingCount === 1 ? '' : 's'}?`}
        description="Here's what these actions will do."
        confirmLabel={pendingApprove?.buttonLabel ?? 'Approve all'}
        cancelLabel='Cancel'
        variant={pendingRisk?.hasDestructive ? 'warning' : 'default'}
        instance='approve-group-confirm'
        onConfirm={onConfirmApprove}
        onCancel={onCancelApprove}
      >
        {pendingRisk && pendingRisk.hasRisk && (
          <div className='divide-y divide-[var(--color-border-secondary)] overflow-hidden rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)]'>
            {pendingRisk.emailCount > 0 && (
              <div className='flex items-center gap-3 px-3 py-2.5'>
                <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] text-fg-secondary'>
                  <Mail01 className='h-4 w-4' />
                </span>
                <span className='text-sm text-fg-secondary'>
                  <span className='font-semibold text-[var(--color-text-primary)]'>
                    {pendingRisk.emailCount}
                  </span>{' '}
                  {pendingRisk.emailCount === 1
                    ? 'email will be sent'
                    : 'emails will be sent'}
                </span>
              </div>
            )}
            {pendingRisk.calendarCount > 0 && (
              <div className='flex items-center gap-3 px-3 py-2.5'>
                <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] text-fg-secondary'>
                  <Calendar className='h-4 w-4' />
                </span>
                <span className='text-sm text-fg-secondary'>
                  <span className='font-semibold text-[var(--color-text-primary)]'>
                    {pendingRisk.calendarCount}
                  </span>{' '}
                  {pendingRisk.calendarCount === 1
                    ? 'calendar invite will be sent'
                    : 'calendar invites will be sent'}
                </span>
              </div>
            )}
            {pendingRisk.destructiveCount > 0 && (
              <div className='flex items-center gap-3 px-3 py-2.5'>
                <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-warning-50 text-warning-600'>
                  <Trash01 className='h-4 w-4' />
                </span>
                <span className='text-sm text-fg-secondary'>
                  <span className='font-semibold text-[var(--color-text-primary)]'>
                    {pendingRisk.destructiveCount}
                  </span>{' '}
                  {pendingRisk.destructiveCount === 1
                    ? 'CRM record will be deleted or merged'
                    : 'CRM records will be deleted or merged'}
                </span>
              </div>
            )}
          </div>
        )}
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={pendingDismiss !== null}
        title={`${pendingDismiss?.tier === 'selected' ? 'Dismiss' : 'Dismiss all'} ${dismissCount} action${dismissCount === 1 ? '' : 's'}?`}
        description="Dismissed actions are cleared from your list and won't be acted on. The AI can suggest them again later."
        confirmLabel={pendingDismiss?.buttonLabel ?? 'Dismiss all'}
        cancelLabel='Cancel'
        variant='default'
        instance='dismiss-group-confirm'
        onConfirm={onConfirmDismiss}
        onCancel={onCancelDismiss}
      />
    </>
  );
}
